import React from 'react';
import PropTypes from 'prop-types';
import autosize from 'autosize';
import { connect } from 'react-redux';
import stringUtils from './../../../../../qti/utils/StringUtils';
import { t } from '../../../../../services/i18nService';
import scorableItemNumberingService from '../../../../../services/questionView/ScorableItemNumberingService';
import marketplacePropertiesService from '../../../../../../src/services/MarketplacePropertiesService';
import UIConstants from '../../../../../constants/UIConstants';
import extendedTextA11ySelector from '../../../../../selectors/question/interactions/extendedTextInteraction/ExtendedTextA11ySelector';
import extendedTextKeyboardService from '../../../../../services/question/interactions/extendedTextInteraction/ExtendedTextKeyboardService';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from '../../../../../services/a11yIIBScreenReaderService';

import styles from './plainTextWrapper.scss';
import TextCounter from './TextCounter/TextCounter';

const ESTIMATED_NORMAL_FONT_LINE_HEIGHT_PLAINTEXT = 19; // approximation
const DEFAULT_NUMBER_OF_LINES_PLAINTEXT = 9;

class ExtendedTextPlainText extends React.Component {
    static propTypes = {
        autoExpand: PropTypes.bool,
        autoSave: PropTypes.func.isRequired,
        contentRevisionId: PropTypes.number.isRequired,
        expectedLines: PropTypes.number,
        fieldWidth: PropTypes.string,
        handleInteractionReady: PropTypes.func.isRequired,
        identifier: PropTypes.string.isRequired,
        isReadOnly: PropTypes.bool,
        maxWords: PropTypes.number,
        shouldSaveOnEmptyChange: PropTypes.bool.isRequired,
        showWordCount: PropTypes.bool,
        userData: PropTypes.string, // The previously stored data
        isResizeEnabled: PropTypes.bool,
        shouldResizeTextarea: PropTypes.bool,
    };

    constructor(props) {
        super(props);
        this.maxChars = parseInt(window.marketplaceProperties.ESSAY_MAX_CHARS, 10) || -1;
        this.maxWords = this.props.maxWords || 0;

        this.state = {
            isDirty: false,
            isEmpty: !this.props.userData || this.props.userData.length === 0,
            text: this.props.userData,
        };
        this.resizeTextarea = this.resizeTextarea.bind(this);
    }

    componentDidMount() {
        this.props.handleInteractionReady(true);
        if (this.props.autoExpand) {
            autosize(this.textarea);
            if (this.props.isResizeEnabled) {
                window.addEventListener('resize', this.resizeTextarea);
            }
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.shouldResizeTextarea && !prevProps.shouldResizeTextarea) {
            this.resizeTextarea();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.resizeTextarea);
    }

    /* There is a known resize issue in autosize lib ' https://github.com/jackmoore/autosize/issues/357 '
        due to which resize in Test environment did not work properly.
    */
    resizeTextarea() {
        if (this.textarea) {
            setTimeout(() => {
                autosize.update(this.textarea);
                this.textarea.style.height = `${this.textarea.scrollHeight}px`;
            }, 100);
        }
    }


    handleTextChanged = (event) => {
        const { autoSave, shouldSaveOnEmptyChange } = this.props;
        const value = event.target.value;

        let textAreaWords = stringUtils.countWords(value);
        const isMaxCharReached = this.reachedMaxCharsLimit(value);

        let text;
        if (this.reachedMaxWordsLimit(textAreaWords) || isMaxCharReached) {
            text = stringUtils.whitespaceSubstring(this.state.text, this.maxChars);
            textAreaWords = stringUtils.countWords(text);

            if (this.pasted) {
                let alertMessage = t('essay.exceedingMaxChars');
                if (isMaxCharReached) {
                    alertMessage += `.\n${t('essay.maxChars', { limit: this.maxChars })}`;
                }
                alert(alertMessage);
            }
        } else {
            text = value;
        }

        this.setState({
            isDirty: true,
            text,
        });
        this.pasted = false;

        if (shouldSaveOnEmptyChange) {
            const isEditorEmpty = value.length === 0;
            if (this.state.isEmpty !== isEditorEmpty) {
                this.setState({
                    isEmpty: isEditorEmpty,
                });
                // timeout is needed here because need to be sure that isDirty state value has changed
                // timeout can be avoided by using functional component approach
                setTimeout(autoSave, 100);
            }
        }
    };

    handleTextPasted = () => {
        this.pasted = true;
    }

    handleKeyDown = (event) => {
        // Only handle enhanced accessibility keys if IIB screen reader support is enabled
        if (!extendedTextA11ySelector.getKeyboardInstructions()) {
            return;
        }

        // Handle accessibility shortcuts (Alt+F10, Esc from toolbar, Enter for submission)
        extendedTextKeyboardService.handleAccessibilityShortcuts(event, {
            returnToEditorCallback: () => {
                // Focus back to this textarea
                if (this.textarea?.focus) {
                    this.textarea.focus();
                }
            },
            submitCallback: () => {
                // Submit the answer
                this.props.autoSave();
            },
        });

        // Keyboard handling is complete - handled by the service
    };

    reachedMaxCharsLimit = text => (this.maxChars > 0 && stringUtils.countChars(text) > this.maxChars);

    reachedMaxWordsLimit = text => (this.maxWords > 0 && text > this.maxWords);

    setAutoSize = () => {
        const containerEl = this.textarea;
        const initialNumberOfLines = this.props.expectedLines || DEFAULT_NUMBER_OF_LINES_PLAINTEXT;
        const height = `${(initialNumberOfLines * ESTIMATED_NORMAL_FONT_LINE_HEIGHT_PLAINTEXT) + 24}px`;

        if (this.props.autoExpand) {
            containerEl.style.minHeight = height;
        } else {
            containerEl.style.height = height;
        }
    };

    /* BEGIN functions called by parent */
    getEditorData = () => {
        this.setState({ isDirty: false });
        return this.state.text;
    };

    /** @param {function} [onReset] runs once the blank is committed. */
    resetEditor = (onReset) => {
        if (this.isEditorInitialized()) {
            this.setState({
                isDirty: true,
                text: '',
            }, onReset);
        }
    };

    isDirty = () => (this.state.isDirty);

    isEditorInitialized = () => (!!this.textarea);
    /* END functions called by parent */

    render() {
        const { isReadOnly = false, expectedLines, contentRevisionId, identifier } = this.props;
        const inlineStyle = {};

        // Custom support for setting field width on text area question type
        if (this.props.fieldWidth) {
            inlineStyle.width = this.props.fieldWidth;
        }
        const interactionNumber = scorableItemNumberingService.getInteractionNumber(contentRevisionId, identifier);
        let scorableItemId;
        if (interactionNumber) {
            scorableItemId = `scorableItem-${contentRevisionId}_${interactionNumber}`;
        }

        const labelTextId = `labelText-${contentRevisionId}_${interactionNumber}`;
        const isA11yV5Enabled = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(
            A11yForIIBScreenReaderIteration.v5
        );
        const questionContentId = `interactionWrapper_${contentRevisionId}`;
        const ariaLabelText = t('interactions.extendedTextPlain.editorWrapperAriaLabel');
        const helperTextId = `${labelTextId}-a11y-helper`;
        const ariaLabelledBy = isA11yV5Enabled
            ? [questionContentId, helperTextId].join(' ')
            : undefined;
        const ariaLabel = isA11yV5Enabled ? undefined : ariaLabelText;

        return (
            <div id={scorableItemId}>
                <textarea
                    id={labelTextId}
                    aria-label={ariaLabel}
                    aria-labelledby={ariaLabelledBy}
                    className={styles.plainText}
                    style={inlineStyle}
                    value={this.state.text}
                    rows={expectedLines || 1}
                    disabled={isReadOnly}
                    onChange={this.props.isReadOnly ? null : this.handleTextChanged}
                    onPaste={this.props.isReadOnly ? null : this.handleTextPasted}
                    onKeyDown={this.props.isReadOnly ? null : this.handleKeyDown}
                    spellCheck="false"
                    ref={(input) => {
                        this.textarea = input;
                    }}
                />
                {isA11yV5Enabled && (
                    <span id={helperTextId} hidden>{ariaLabelText}</span>
                )}

                <TextCounter
                    text={this.state.text}
                    showWordCount={this.props.showWordCount}
                    maxWords={this.maxWords}
                    maxChars={this.maxChars}
                />
            </div>
        );
    }
}


const mapStateToProps = (state) => {
    const isResizeEnabled = marketplacePropertiesService.isEssayResizeEnabledForCandidate();
    return {
        isResizeEnabled,
        shouldResizeTextarea: isResizeEnabled && state.ui.stimulusState === UIConstants.STIMULUS_STATE.DROP,
    };
};
export default connect(mapStateToProps, null, null, { withRef: true })(ExtendedTextPlainText);
